import { useState } from 'react';
import { FileProtectOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';

import { DataTable, MenuThaoTac, PageHeader, TabsTruot } from '../../../components/shared';
import { Badge, Button, Input } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { HopNopHoSo } from '../components/HopNopHoSo';
import { HopXacNhanXoa } from '../components/HopXacNhanXoa';
import { VungDuLieu } from '../components/VungDuLieu';
import { KET_LUAN, QUYEN, TRANG_THAI_PHIEN } from '../constants';
import { usePhien, useXoaPhien } from '../hooks/useThamDinh';
import type { KetLuan, Phien, TrangThaiPhien } from '../types';

/**
 * Bộ lọc trạng thái, theo giao diện v3: tab ngang thay vì ô chọn.
 *
 * `dang_xu_ly` gộp ba trạng thái đang chạy (đặc tả mục 13.4) — người dùng hỏi "cái nào máy đang làm", không
 * quan tâm nó đang đọc tệp hay đang chấm. `cho_xu_ly`, `dang_doc`, `dang_cham` vì vậy không có tab riêng.
 */
const TAB = ['tat-ca', 'dang_xu_ly', 'cho_duyet', 'cho_phe_duyet', 'hoan_tat', 'loi'] as const;
type Tab = (typeof TAB)[number];

/**
 * Hồ sơ thẩm định (`/client/tham-dinh/ho-so`), theo giao diện v3.
 *
 * Một hồ sơ nộp lên hiện ở trạng thái "Chờ thẩm định viên" sau khi trích xong văn bản. Bước chấm cần client
 * mô hình (Mốc 4 của đặc tả §16, đợt 3), nên cột Điểm và Kết luận còn trống — đúng như backend trả về, không
 * giả vờ đang chạy.
 */
export function HoSoThamDinhView() {
  const { t, locale } = useT();
  const q = useModulePermissions(QUYEN);
  const dieuHuong = useDieuHuong();
  const [nhap, setNhap] = useState('');
  const [tim, setTim] = useState('');
  const [tab, setTab] = useState<Tab>('tat-ca');
  const [moNop, setMoNop] = useState(false);
  const [xoa, setXoa] = useState<Phien | undefined>();

  const ds = usePhien({
    search: tim || undefined,
    trang_thai: tab === 'tat-ca' ? undefined : tab,
  });
  const xoaPhien = useXoaPhien();
  const coLoc = Boolean(tim) || tab !== 'tat-ca';

  const cot: TableProps<Phien>['columns'] = [
    {
      title: t('thamDinh.phien.cotTieuDe'),
      dataIndex: 'tieu_de',
      render: (_, r) => (
        <button
          type="button"
          className="block min-w-0 max-w-full text-start"
          onClick={() => dieuHuong(`/tham-dinh/ho-so/${r.id}`)}
        >
          <span className="block truncate font-medium text-primary-700 hover:underline">{r.tieu_de}</span>
          {/* Bộ tiêu chí gộp vào dòng phụ như v3, không chiếm một cột riêng. */}
          <span className="block truncate text-2xs text-content-muted">{r.bo_ten}</span>
        </button>
      ),
    },
    {
      title: t('thamDinh.phien.cotTrangThai'),
      dataIndex: 'trang_thai',
      width: 170,
      render: (v: TrangThaiPhien) => (
        <Badge variant={TRANG_THAI_PHIEN[v].variant}>{t(TRANG_THAI_PHIEN[v].khoa)}</Badge>
      ),
    },
    {
      title: t('thamDinh.phien.cotDiem'),
      dataIndex: 'diem',
      width: 80,
      align: 'end',
      render: (v: number | null) => (v === null ? KHONG_CO : soVN(v, locale)),
    },
    {
      title: t('thamDinh.phien.cotKetLuan'),
      dataIndex: 'ket_luan',
      width: 160,
      render: (v: KetLuan | null) =>
        v ? <Badge variant={KET_LUAN[v].variant}>{t(KET_LUAN[v].khoa)}</Badge> : <span className="text-content-muted">{KHONG_CO}</span>,
    },
    {
      title: t('thamDinh.phien.cotCapNhat'),
      dataIndex: 'created_at',
      width: 150,
      render: (v: string) => ngayGioVN(v, locale),
    },
    {
      title: t('chung.thaoTac'),
      key: 'thao-tac',
      width: 72,
      align: 'center',
      render: (_, r) => (
        <MenuThaoTac
          tenBanGhi={r.tieu_de}
          muc={[
            {
              khoa: 'xem',
              nhan: t('thamDinh.phien.xem'),
              onChon: () => dieuHuong(`/tham-dinh/ho-so/${r.id}`),
            },
            {
              khoa: 'xoa',
              nhan: t('thamDinh.phien.xoa'),
              // Đang chấm thì backend chặn (worker còn ghi vào nó); ẩn mục thay vì để bấm rồi nhận 409.
              hien: q.phienXoa && !['cho_xu_ly', 'dang_doc', 'dang_cham'].includes(r.trang_thai),
              onChon: () => setXoa(r),
            },
          ]}
        />
      ),
    },
  ];

  const bang = (
    <VungDuLieu
      dangTai={ds.isPending}
      loi={ds.error}
      dulieu={ds.data}
      rong={(ds.data?.items.length ?? 0) === 0}
      onThuLai={() => void ds.refetch()}
      rongIcon={FileProtectOutlined}
      rongTieuDe={coLoc ? t('thamDinh.phien.rongLoc') : t('thamDinh.phien.rong')}
      rongMo={coLoc ? undefined : t('thamDinh.phien.rongMo')}
      rongHanhDong={
        !coLoc && q.phienTao ? (
          <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setMoNop(true)}>
            {t('thamDinh.nop.nutMoi')}
          </Button>
        ) : undefined
      }
    >
      {(du) => (
        <DataTable<Phien>
          caption={t('thamDinh.phien.caption')}
          columns={cot}
          dataSource={du.items}
          pagination={false}
        />
      )}
    </VungDuLieu>
  );

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.thamDinhAi') }, { label: t('nav.muc.hoSoThamDinh') }]}
          titleIcon={<FileProtectOutlined className="text-primary-600" />}
          title={t('thamDinh.hoSoTieuDe')}
          subtitle={t('thamDinh.hoSoMo')}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void ds.refetch()}
              >
                <ReloadOutlined className={ds.isFetching ? 'animate-spin' : ''} />
              </Button>
              {q.phienTao && (
                <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setMoNop(true)}>
                  {t('thamDinh.nop.nutMoi')}
                </Button>
              )}
            </>
          }
        />

        <div className="rounded-lg border border-default bg-surface p-4 shadow-sm">
          <div className="mb-3 flex justify-end">
            <Input
              allowClear
              className="w-full sm:w-72"
              prefix={<SearchOutlined className="text-content-muted" />}
              placeholder={t('thamDinh.phien.tim')}
              aria-label={t('thamDinh.phien.tim')}
              value={nhap}
              onChange={(e) => {
                setNhap(e.target.value);
                if (!e.target.value) setTim('');
              }}
              onPressEnter={() => setTim(nhap.trim())}
              onBlur={() => setTim(nhap.trim())}
            />
          </div>

          <TabsTruot
            gon
            defaultActiveKey={tab}
            onChange={(k) => setTab(k as Tab)}
            items={TAB.map((k) => ({
              key: k,
              label:
                k === 'tat-ca'
                  ? t('thamDinh.phien.tabTatCa')
                  : k === 'dang_xu_ly'
                    ? t('thamDinh.phien.dangXuLy')
                    : t(TRANG_THAI_PHIEN[k].khoa),
              // Mọi tab dùng chung một bảng: đổi tab chỉ đổi tham số lọc, không dựng lại cây component.
              children: bang,
            }))}
          />
        </div>
      </div>

      <HopNopHoSo open={moNop} onClose={() => setMoNop(false)} />

      <HopXacNhanXoa
        open={Boolean(xoa)}
        onClose={() => {
          setXoa(undefined);
          xoaPhien.reset();
        }}
        tieuDe={t('thamDinh.phien.xoaHoiTieuDe')}
        than={t('thamDinh.phien.xoaHoiThan', { ten: xoa?.tieu_de ?? '' })}
        nhanXacNhan={t('thamDinh.phien.xoaXacNhan')}
        dangChay={xoaPhien.isPending}
        loi={xoaPhien.error}
        onXacNhan={() =>
          xoa && xoaPhien.mutate({ id: xoa.id, ten: xoa.tieu_de }, { onSuccess: () => setXoa(undefined) })
        }
      />
    </div>
  );
}
